import React from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  updateBulkOrderDropOff,
  updateDispatchOrderStatus,
  updateOrderDropOff,
} from "../../../redux/features/orderSlice";

interface ConfirmDispatchProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: any;
  dispatchDetails: any;
  contractId?: string;
  agentId?: string;
}

const ConfirmDispatch: React.FC<ConfirmDispatchProps> = ({
  isOpen,
  onClose,
  dispatchDetails,
  onSave,
  contractId,
  agentId,
}) => {
  const { loading } = useAppSelector((state) => state.orders);
  const dispatch = useAppDispatch();

  const handleConfirm = async () => {
    try {
      if (dispatchDetails?.dispatchStatus === "Dispatch") {
        const dispatchDetailsFilter = {
          courierOfficeId: dispatchDetails?.courierOfficeId,
          actualCourierFee: dispatchDetails?.actualCourierFee,
        };
        if (contractId) {
          await dispatch(
            updateOrderDropOff({
              contractId,
              dropOfDetails: dispatchDetailsFilter, // Fix any typo here
            })
          ).unwrap();
        }
      } else if (agentId && dispatchDetails?.dispatchStatus === "Arrived") {
        await dispatch(
          updateBulkOrderDropOff({ agentId, dropOfDetails: dispatchDetails })
        ).unwrap();
      } else {
        const dispatchDetailsFilter = {
          courierOfficeId: dispatchDetails?.courierOfficeId,
          dispatchStatus: dispatchDetails?.dispatchStatus,
          orderId: dispatchDetails?.orderId,
        };
        await dispatch(
          updateDispatchOrderStatus(dispatchDetailsFilter)
        ).unwrap();
      }

      // These will only be called if the dispatch operations are successful
      toast.success("Order dispatch status updated successfully!");
      onClose && onClose(); // Close the modal only on success
      onSave && onSave();
    } catch (error: any) {
      console.error("Failed to update order dispatch status:", error);
      toast.error(
        error?.message || "An error occurred while dispatching the order."
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md rounded-lg p-6 shadow-lg">
        {/* Icon Section */}
        <div className="flex justify-center mb-4">
          <div className="bg-blue-100 text-primary rounded-full p-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-8 h-8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14 10H10m4 0H8a1 1 0 01-1-1V5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 001 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4m6 0h2M6 14H4"
              />
            </svg>
          </div>
        </div>

        {/* Title Section */}
        <h2 className="text-center text-xl font-semibold mb-2">
          Confirm Dispatch
        </h2>

        {/* Description Section */}
        <p className="text-center text-gray-600 mb-6">
          Are you sure you want to dispatch this order with status{" "}
          <strong>{dispatchDetails.dispatchStatus}</strong>?
        </p>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <button
            onClick={onClose}
            className="w-1/2 bg-gray-100 text-gray-800 rounded-md py-2 px-4 mr-2 hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="w-1/2 bg-primary text-white rounded-md py-2 px-4 hover:bg-primary"
          >
            {loading ? <PulseLoader size={6} color="#fff" /> : "Yes, Dispatch"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDispatch;
